import {
  SESSION_HISTORY_STATUS,
  type MemberSessionHistoryItemDto,
  type MemberSessionHistorySummaryDto,
  type SessionHistoryFavoriteSlotDto,
  type SessionHistoryProgramDto,
} from "@/modules/members/gateway/memberDto"
import dayjs from "dayjs"

// ────────────────────────────────── Summary Entity ──────────────────────────────────

export class MemberSessionHistorySummaryEntity {
  constructor(
    public readonly totalSessions: number,
    public readonly attendedCount: number,
    public readonly noShowCount: number,
    public readonly programs: readonly SessionHistoryProgramDto[],
    public readonly favoriteSlots: readonly SessionHistoryFavoriteSlotDto[],
  ) {}

  static fromDto(dto: MemberSessionHistorySummaryDto): MemberSessionHistorySummaryEntity {
    return new MemberSessionHistorySummaryEntity(
      dto.pageCounter.all,
      dto.pageCounter.present,
      dto.pageCounter.noShow,
      dto.resume,
      dto.favorites,
    )
  }

  getProgramPercentage(programName: string): number {
    if (this.totalSessions === 0) return 0
    const program = this.programs.find((p) => p.name === programName)
    if (!program) return 0
    return Math.round((program.count / this.totalSessions) * 100)
  }
}

// ────────────────────────────────── Item Entity ──────────────────────────────────

export class MemberSessionHistoryItemEntity {
  constructor(
    public readonly id: string,
    public readonly startDate: string,
    public readonly endDate: string,
    public readonly programName: string,
    public readonly programColor: string,
    public readonly coachFullName: string | null,
    public readonly status: SESSION_HISTORY_STATUS,
    public readonly offerName: string,
    public readonly offerType: string,
    public readonly location: string | null,
    public readonly bookedAt: string,
    public readonly archived: boolean,
  ) {}

  static fromDto(dto: MemberSessionHistoryItemDto): MemberSessionHistoryItemEntity {
    return new MemberSessionHistoryItemEntity(
      `${dto.startDate}-${dto.name}`, // Generated key
      dto.startDate,
      dto.endDate,
      dto.name,
      dto.color,
      dto.coachName,
      dto.status,
      dto.offerName,
      dto.offerType,
      dto.location,
      dto.bookedAt,
      dto.archived,
    )
  }

  getFormattedDate(): string {
    return dayjs(this.startDate).format("L")
  }

  getStartTime(): string {
    return dayjs(this.startDate).format("LT")
  }

  getTimeRange(): string {
    const start = dayjs(this.startDate)
    const end = dayjs(this.endDate)
    return `${start.format("LT")}-${end.format("LT")}`
  }

  getDayOfWeek(): string {
    return dayjs(this.startDate).format("dddd")
  }

  getCoachFullName(): string | null {
    return this.coachFullName
  }

  isAttended(): boolean {
    return this.status === SESSION_HISTORY_STATUS.PRESENT
  }

  isNoShow(): boolean {
    return this.status === SESSION_HISTORY_STATUS.NO_SHOW
  }
}
